// Create-or-reuse rules for people rows, mirrored from the API's contract
// (PEOPLE_EMAIL_ADDENDUM): email match wins over name match, and a name match
// with a still-empty email is the backfill path — re-posting that name with an
// email fills it in place without creating a second row.
//
// One deliberate tightening over the addendum's step 2: a name match against a
// row that already carries a DIFFERENT email is NOT a reuse. Two directory
// people can share a display name; collapsing them onto one row would route
// the second person's Slack nudges to the first. (The API still reuses by
// name in that case — the store detects the mismatch on the response and
// refuses it loudly rather than silently accepting the wrong person.)
//
// Pure so it can be tested without the store's network + React plumbing.

export type PersonLike = { id: string; name: string; email?: string | null };

export type ReuseDecision<T extends PersonLike> = {
  match: T | null;
  // True when the matched row has no email but the caller supplied one — the
  // store must still POST so the server fills the email on the existing row.
  backfillEmail: boolean;
};

export function normalizeEmail(email: string | null | undefined): string | null {
  const e = email?.trim().toLowerCase();
  return e && e.includes("@") ? e : null;
}

export function resolvePersonReuse<T extends PersonLike>(
  roster: readonly T[],
  name: string,
  email: string | null | undefined
): ReuseDecision<T> {
  const normEmail = normalizeEmail(email);
  const normName = name.trim().toLowerCase();

  if (normEmail) {
    const byEmail = roster.find(p => normalizeEmail(p.email) === normEmail);
    if (byEmail) return { match: byEmail, backfillEmail: false };
  }

  const byName = roster.find(p => p.name.trim().toLowerCase() === normName) ?? null;
  if (!byName) return { match: null, backfillEmail: false };
  const rowEmail = normalizeEmail(byName.email);
  // Same name, different known email: a different person. New row.
  if (normEmail && rowEmail && rowEmail !== normEmail) return { match: null, backfillEmail: false };
  return { match: byName, backfillEmail: !!normEmail && !rowEmail };
}

// Did the server answer our create-or-reuse with a DIFFERENT person than we
// asked for? True when we sent an email and the returned row carries another
// one — the API matched by name (its step 2) across two distinct people.
export function isDifferentPerson(sentEmail: string | null, saved: { email?: string | null }): boolean {
  const savedEmail = normalizeEmail(saved.email);
  return !!sentEmail && !!savedEmail && savedEmail !== sentEmail;
}
